import React, { useEffect, useReducer } from 'react';

import { CircularProgress } from '@mui/material';
import styled from 'styled-components';

export interface CrossfadeImageProps {
    src: string;
}

const Container = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: ${(props) => props.theme.colors.zoomGrey};
    overflow: hidden;
    z-index: 1;
    
    img {
        position: absolute;
        width: 100%;
        height: 100%;
        object-fit: contain;
    };
`;

const TopImage = styled.img`
    position: relative;
    z-index: 2;
`;

const BottomImage = styled.img`
    position: relative;
    z-index: 1;
`;

const ImageLoader = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
`;

const StyledCircularProgress = styled(CircularProgress)`
    color: ${(props) => props.theme.colors.white};
`;

const reducer = (state, action) => {
    switch (action.type) {
        case 'elevate-bottom': return { ...state, top: state.bottom };
        case 'update-bottom': return { ...state, bottom: action.payload };
        case 'disappear-top': return { ...state, opacity: 0, error: false };
        case 'reappear-top': return { ...state, opacity: 1, error: false };
        case 'error': return { ...state, error: true };
        default: return state;
    }
};

const CrossfadeImage: React.FC<CrossfadeImageProps> = ({ src }) => {
    const transitionDuration = 500;

    const initialState = {
        top: src,
        bottom: src,
        opacity: 0,
        error: false
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        if (src === undefined) {
            dispatch({ type: 'error' });
        } else {
            dispatch({ type: 'update-bottom', payload: src });
        }
    }, [src]);

    useEffect(() => {
        if (state.opacity !== 0) return;
        if (state.error) return;

        const id = setTimeout(() => {
            dispatch({ type: 'elevate-bottom' });
        }, transitionDuration + 100); // Give the transition time to happen

        return () => clearTimeout(id);
    }, [state]);

    const onBottomLoad = () => {
        dispatch({ type: 'disappear-top' });
    };

    const onTopLoad = () => {
        dispatch({ type: 'reappear-top' });
    };

    const onError = () => {
        if (state.error) return;

        dispatch({ type: 'error' });
    };

    return (
        <Container>
            {state.error &&
                <ImageLoader>
                    <div>
                        <StyledCircularProgress />
                    </div>
                </ImageLoader>
            }
            {state.top &&
                <TopImage
                    src={state.top}
                    alt='Breakout Image'
                    onLoad={onTopLoad}
                    onError={onError}
                    style={{
                        opacity: state.opacity,
                        transition: `opacity ${transitionDuration}ms ease`
                    }}
                />
            }
            {state.bottom &&
                <BottomImage
                    src={state.bottom}
                    onLoad={onBottomLoad}
                    onError={onError}
                    alt='Breakout Image'
                    style={{
                        display: state.error ? 'none' : 'inline-block'
                    }}
                />
            }
        </Container>
    );
};

export default CrossfadeImage;
